import { useRef } from 'react';
import { Navigation } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { getActionAriaLabel, getLinkAriaLabel } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { handleSubmenuArrowNavigation } from '@/components/Megamenu/helpers';
import type {
  ContentfulMainMenuItemFragment,
  ContentfulScrollerItemFragment,
} from '@/graphql/contentful/api';
import { useRegionProvider } from '@/providers/RegionProvider';

import { ReactComponent as ChevronLeft } from '@/assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRight } from '@/assets/icons/chevron-right.svg';

import * as S from './Scroller.styles';

export interface ScrollerProps {
  menuItem: ContentfulMainMenuItemFragment;
  onNavigate?: () => void;
  variant?: 'desktop' | 'mobile';
}

export const Scroller = ({
  menuItem,
  onNavigate,
  variant = 'desktop',
}: ScrollerProps) => {
  const { country, locale } = useRegionProvider();
  const items = menuItem.scrollerItemsCollection?.items;
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  if (!items?.length) return null;

  const countryCode = country.code.toLowerCase();

  const visibleItems = items.filter(
    (item): item is ContentfulScrollerItemFragment => {
      if (item === null) return false;
      const localization = item.localizationConfiguration;
      if (Array.isArray(localization) && localization.length > 0) {
        if (!localization.includes(countryCode)) return false;
      }
      return variant === 'desktop'
        ? item.visibleInDesktop !== false
        : item.visibleInMobile !== false;
    },
  );

  if (visibleItems.length === 0) return null;

  if (variant === 'mobile') {
    return (
      <S.MobileScrollerWrapper data-test-id='Scroller-mobile'>
        {menuItem.scrollerLabel && (
          <S.MobileScrollerLabel>
            {menuItem.scrollerLabel}
          </S.MobileScrollerLabel>
        )}
        <S.MobileScrollerList>
          {visibleItems.map((item) => (
            <S.MobileScrollerListItem key={item.sys.id}>
              <S.MobileScrollerItemLink
                aria-label={
                  getLinkAriaLabel(item.url ?? '', locale.code) ||
                  item.label ||
                  undefined
                }
                data-test-id={`ScrollerItem-${item.internalName}`}
                href={item.url ?? ''}
                onClick={onNavigate}
                onKeyDown={(e) =>
                  handleSubmenuArrowNavigation(e, {
                    itemSelector: '[data-test-id^="ScrollerItem-"]',
                    nextKey: 'ArrowRight',
                    prevKey: 'ArrowLeft',
                    submenuSelector: '[data-test-id="Scroller-mobile"]',
                  })
                }
                rel={item.openUrlInNewTab ? 'noopener noreferrer' : undefined}
                target={item.openUrlInNewTab ? '_blank' : '_self'}
              >
                <S.MobileScrollerItemImageWrapper>
                  <AspectRatioImage
                    alt={item.image?.title ?? item.label ?? ''}
                    aspectRatio={{ x: 4, y: 5 }}
                    focalPoint={{
                      x: (item.image?.width ?? 0) / 2,
                      y: (item.image?.height ?? 0) / 2,
                    }}
                    loaderProps={{ height: 105 }}
                    loading='lazy'
                    originalImageDimensions={{
                      x: item.image?.width ?? 0,
                      y: item.image?.height ?? 0,
                    }}
                    showFallbackPlaceholder
                    sizes='30vw'
                    src={item.image?.url ?? ''}
                    useLoader
                    width='100%'
                  />
                </S.MobileScrollerItemImageWrapper>
                <S.MobileScrollerItemLabel>
                  {item.label}
                </S.MobileScrollerItemLabel>
              </S.MobileScrollerItemLink>
            </S.MobileScrollerListItem>
          ))}
        </S.MobileScrollerList>
      </S.MobileScrollerWrapper>
    );
  }

  return (
    <S.ScrollerSection data-test-id='Scroller-desktop'>
      {menuItem.scrollerLabel && (
        <S.ScrollerLabel>{menuItem.scrollerLabel}</S.ScrollerLabel>
      )}
      <S.SwiperWrapper>
        <S.StyledSwiper
          modules={[Navigation]}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            if (
              swiper.params.navigation &&
              typeof swiper.params.navigation !== 'boolean'
            ) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          slidesPerView='auto'
          spaceBetween={16}
          watchSlidesProgress
        >
          {visibleItems.map((item) => (
            <SwiperSlide key={item.sys.id}>
              <S.ScrollerItemLink
                aria-label={
                  getLinkAriaLabel(item.url ?? '', locale.code) ||
                  item.label ||
                  undefined
                }
                data-test-id={`ScrollerItem-${item.internalName}`}
                href={item.url ?? ''}
                onClick={onNavigate}
                onKeyDown={(e) =>
                  handleSubmenuArrowNavigation(e, {
                    itemSelector: '[data-test-id^="ScrollerItem-"]',
                    nextKey: 'ArrowRight',
                    prevKey: 'ArrowLeft',
                    submenuSelector: '[data-test-id="Scroller-desktop"]',
                  })
                }
                rel={item.openUrlInNewTab ? 'noopener noreferrer' : undefined}
                target={item.openUrlInNewTab ? '_blank' : '_self'}
              >
                <S.ScrollerItemImageWrapper>
                  <AspectRatioImage
                    alt={item.image?.title ?? item.label ?? ''}
                    aspectRatio={{ x: 4, y: 5 }}
                    focalPoint={{
                      x: (item.image?.width ?? 0) / 2,
                      y: (item.image?.height ?? 0) / 2,
                    }}
                    loaderProps={{ height: 200 }}
                    loading='lazy'
                    originalImageDimensions={{
                      x: item.image?.width ?? 0,
                      y: item.image?.height ?? 0,
                    }}
                    showFallbackPlaceholder
                    sizes='16vw'
                    src={item.image?.url ?? ''}
                    useLoader
                    width='100%'
                  />
                </S.ScrollerItemImageWrapper>
                <S.ScrollerItemLabel>{item.label}</S.ScrollerItemLabel>
              </S.ScrollerItemLink>
            </SwiperSlide>
          ))}
        </S.StyledSwiper>
        <S.NavigationButton
          ref={prevRef}
          $type='prev'
          aria-label={
            getActionAriaLabel('previous_page', locale.code) || 'Previous'
          }
          data-test-id='ScrollerNavPrev'
        >
          <ChevronLeft aria-hidden focusable={false} />
        </S.NavigationButton>
        <S.NavigationButton
          ref={nextRef}
          $type='next'
          aria-label={getActionAriaLabel('next_page', locale.code) || 'Next'}
          data-test-id='ScrollerNavNext'
        >
          <ChevronRight aria-hidden focusable={false} />
        </S.NavigationButton>
      </S.SwiperWrapper>
    </S.ScrollerSection>
  );
};

Scroller.displayName = 'Scroller';
